import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, GraduationCap, Heart, Sprout, BookOpen, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const programs = [
    {
      icon: <Sprout className="w-10 h-10" />,
      title: 'Adopt-a-Mile',
      description: 'Community cleanup and environmental stewardship programs that keep our neighborhoods clean and beautiful.',
      image: 'https://customer-assets.emergentagent.com/job_1715fc91-82d5-4329-a769-57f0c42f446b/artifacts/tay18eyj_1000036514.jpg'
    },
    {
      icon: <GraduationCap className="w-10 h-10" />,
      title: 'STEM Day',
      description: 'Engaging youth in science, technology, engineering, and mathematics through hands-on learning experiences.',
      image: 'https://customer-assets.emergentagent.com/job_1715fc91-82d5-4329-a769-57f0c42f446b/artifacts/hsdtwdgc_1000036507.jpg'
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Financial Literacy',
      description: 'Empowering individuals with essential financial education and money management skills for long-term success.',
      image: 'https://customer-assets.emergentagent.com/job_1715fc91-82d5-4329-a769-57f0c42f446b/artifacts/m7qrlw6q_1000036513.jpg'
    },
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'Community Service',
      description: 'Volunteer-driven initiatives that strengthen neighborhoods and support families in need.',
      image: 'https://customer-assets.emergentagent.com/job_1715fc91-82d5-4329-a769-57f0c42f446b/artifacts/g7e5jc4q_1000036511.jpg'
    }
  ];

  const stats = [
    { number: '15+', label: 'Years of Service' },
    { number: '100+', label: 'Families Helped' },
    { number: '50+', label: 'Community Events' },
    { number: '$25K+', label: 'Community Investment' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca"
            alt="Community coming together"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/90 to-blue-900/95"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Empowering Communities
            <span className="block text-yellow-400 mt-2">Through Service</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-2 max-w-3xl mx-auto leading-relaxed">
            EPPS Helps exists to empower individuals and communities through service, financial education, and mentoring.
          </p>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Emmett Pollard Philanthropy Society
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/donate">
              <Button className="bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-white px-8 py-6 text-lg rounded-lg font-semibold shadow-2xl hover:shadow-yellow-500/50 transform hover:scale-105 transition-all duration-300">
                Donate Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/about">
              <Button className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-6 text-lg rounded-lg font-semibold transform hover:scale-105 transition-all duration-300">
                Learn More
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Mission & Vision</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-yellow-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Mission Statement</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  EPPS Helps exists to empower individuals and communities through service, financial education, and mentoring, with programs designed to advance economic opportunity and social mobility.
                </p>
                <p className="text-sm text-gray-600 mt-4 italic">
                  — Emmett Pollard Philanthropy Society
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-xl flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  A community-based nonprofit organization focused on strengthening individuals, families, and neighborhoods through education, service, and collaborative initiatives that support long-term economic and social resilience.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-blue-50 to-gray-50 rounded-2xl">
            <p className="text-gray-800 leading-relaxed text-lg">
              We work alongside schools, local organizations, and civic partners to deliver practical programs that promote financial literacy, youth development, and community engagement. Our approach emphasizes accessibility, measurable impact, and sustained relationships—particularly in communities where resources and opportunity are unevenly distributed.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.number}</div>
                <div className="text-blue-100 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Programs</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Making a lasting impact through targeted initiatives that address real community needs
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-yellow-600 mx-auto mt-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {programs.map((program, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                <div className="relative h-64 overflow-hidden bg-gray-100">
                  <img
                    src={program.image}
                    alt={program.title}
                    className="w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-4 left-4 w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-lg">
                    {program.icon}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{program.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{program.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500 rounded-full filter blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Make a Difference?</h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Your support helps us continue empowering communities and changing lives. Every contribution makes an impact.
          </p>
          <Link to="/donate">
            <Button className="bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-white px-10 py-6 text-lg rounded-lg font-semibold shadow-2xl hover:shadow-yellow-500/50 transform hover:scale-105 transition-all duration-300">
              Support Our Mission
              <Heart className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
